import events from "add-event-listener";
import * as React from "react";

import {
    UnitLayoutingContext,
    unitLayoutingContextTypes,
} from "../../FormParts/Unit/Internals/UnitBorderContext/UnitLayoutingContext";
import { themed } from "../../Internals/ThemeProvider/ThemedWorkaround";
import { pullContext } from "../../TypingUtils";

import * as styles from "./Sticky.styles";

interface StickyProps {
    children: React.ReactChild;
    allWidth?: boolean;
    background: string;
    horizontalScroll: number;
    isLastRow?: boolean;
    position?: string;
    width: number;
}

type StickyPosition = "static" | "fixed" | "absolute";

interface StickyState {
    position: StickyPosition;
    width: string | number;
}

export class Sticky extends React.Component<StickyProps, StickyState> {
    public static defaultProps = {
        allWidth: false,
        changeHeightRows: false,
        position: "bottom",
        width: 0,
        isLastRow: false,
    };

    public static contextTypes = unitLayoutingContextTypes;

    public state: StickyState = { position: "static", width: "auto" };

    private readonly _element = React.createRef<HTMLDivElement>();
    private readonly _root = React.createRef<HTMLDivElement>();
    private readonly _childrenContainer = React.createRef<HTMLDivElement>();
    private heightElement?: number;
    private stop?: HTMLElement;

    public componentDidMount(): void {
        events.addEventListener(window, "scroll", this._changePosition);
        events.addEventListener(window, "resize", this._changePosition);

        if (this._element.current != undefined) {
            const element = this._element.current.getBoundingClientRect();
            this.heightElement = element.bottom - element.top;
        }
    }

    public UNSAFE_componentWillReceiveProps(): void {
        this._changePosition();
    }

    public shouldComponentUpdate(nextProps: StickyProps, nextState: StickyState): boolean {
        const meaningfulProps: Array<keyof Partial<StickyProps>> = ["width", "horizontalScroll"];
        return (
            meaningfulProps.some(prop => nextProps[prop] !== this.props[prop]) ||
            nextState.position !== this.state.position ||
            nextState.width !== this.state.width
        );
    }

    public componentWillUnmount(): void {
        events.removeEventListener(window, "scroll", this._changePosition);
        events.removeEventListener(window, "resize", this._changePosition);
    }

    public _changePosition = () => {
        if (this.props.position === "top") {
            this._changeTopPosition();
        } else {
            this._changeBottomPosition();
        }
    };

    public _changeBottomPosition = () => {
        if (this._root.current == undefined) {
            return;
        }

        const rootBottom = this._root.current.getBoundingClientRect().bottom;
        const screenHeight = document.documentElement.offsetHeight;
        let position: StickyPosition = "static";

        if (this.stop && this.stop.getBoundingClientRect().top - (screenHeight - (this.heightElement ?? 0)) > 0) {
            position = "absolute";
        } else if (rootBottom > screenHeight) {
            position = "fixed";
        }

        this.setState({ position: position });
    };

    public _changeTopPosition = () => {
        if (this._root.current == undefined) {
            return;
        }
        let childrenWidth: string | number = "auto";
        const paddings = this.getContext().paddingRight + this.getContext().paddingLeft || 0;

        const firstChild = this._childrenContainer.current?.children[0];
        if (firstChild != undefined) {
            const childrenRect = firstChild.getBoundingClientRect();
            childrenWidth = childrenRect.right - childrenRect.left;
        }

        const rootRect = this._root.current.getBoundingClientRect();
        const width = rootRect.right - rootRect.left - paddings;

        if (this.stop && this.stop.getBoundingClientRect().top - (this.heightElement ?? 0) < 0) {
            this.setState({
                position: "absolute",
                width: width,
            });

            return;
        }

        if (rootRect.top < 0) {
            this.setState({
                position: "fixed",
                width: width,
            });

            return;
        }

        this.setState({
            position: "static",
            width: childrenWidth,
        });
    };

    public setStop(element: HTMLElement | undefined): void {
        if (element != undefined && this.stop == undefined) {
            this.stop = element;
            this._changePosition();
        }
    }

    public getStickyStyle = () => {
        const stickyStyle: React.CSSProperties = {
            position: this.state.position,
            width: this.state.width,
            zIndex: 500,
            backgroundColor: this.props.background,
        };

        if (this.props.allWidth || (this.props.position === "top" && this.state.position === "fixed")) {
            stickyStyle.paddingLeft = this.getContext().paddingLeft || 0;
            stickyStyle.paddingRight = this.getContext().paddingRight || 0;
        }

        if (this.props.position === "top") {
            if (this.state.position === "absolute") {
                stickyStyle.top = (this.stop?.offsetTop ?? 9) - (this.heightElement ?? 0);
            } else {
                stickyStyle.top = 0;
            }
        } else if (this.state.position === "absolute") {
            if (this.stop) {
                stickyStyle.top = this.stop?.offsetTop ?? 0;
            }
        } else {
            stickyStyle.bottom = 0;
        }

        if (this.props.allWidth) {
            const paddings = this.getContext().paddingRight + this.getContext().paddingLeft || 0;

            stickyStyle.width = this.props.width - paddings || "auto";
        }

        return stickyStyle;
    };

    public getStickyClassNames = () => {
        const isTopView = this.props.position === "top";
        const isFixed = this.state.position === "fixed";
        const isAbsolute = this.state.position === "absolute";
        const needScroll = Boolean(this.props.horizontalScroll);
        const allWidth = this.props.allWidth;
        const isLastRow = this.props.isLastRow;

        return themed(
            styles.horizontalScroll(
                needScroll,
                !isTopView && (isFixed || isAbsolute),
                allWidth ?? false,
                (allWidth && isLastRow && !needScroll) ?? false,
                isTopView && isFixed
            )
        );
    };

    public render(): JSX.Element {
        const stickyStyle = this.getStickyStyle();

        const stickyClassNames = this.getStickyClassNames();
        const rootStyle: React.CSSProperties = { height: this.heightElement || 0 };

        if (this.props.allWidth || (this.props.position === "top" && this.state.position === "fixed")) {
            rootStyle.marginLeft = -this.getContext().paddingLeft || 0;
            rootStyle.marginRight = -this.getContext().paddingRight || 0;
        }

        return (
            <div ref={this._root} style={rootStyle}>
                <div css={stickyClassNames} ref={this._element} style={stickyStyle}>
                    <div
                        css={themed(
                            styles.contentContainer,
                            this.props.position === "top" && this.state.position !== "fixed" && styles.header
                        )}
                        ref={this._childrenContainer}>
                        {this.props.children}
                    </div>
                </div>
            </div>
        );
    }

    private getContext(): UnitLayoutingContext {
        return pullContext<UnitLayoutingContext>(this);
    }
}
